import { useState } from "react";

import { format, parseISO, startOfWeek, endOfWeek, eachWeekOfInterval, subDays } from "date-fns";
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface ActivityData {
  activity_date: string;
  sessions_created: number;
  messages_sent: number;
  memories_created: number;
  projects_created?: number;
  boards_created?: number;
  total_activity: number;
}

interface ActivityTimelineProps {
  data: ActivityData[];
  title: string;
  description?: string;
  showProjects?: boolean;
  showBoards?: boolean;
}

export const ActivityTimeline = ({ data, title, description, showProjects = true, showBoards = false }: ActivityTimelineProps) => {
  const [timeRange, setTimeRange] = useState<"7d" | "30d" | "90d" | "365d">("30d");

  const getFilteredData = () => {
    const days = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : timeRange === "90d" ? 90 : 365;
    const cutoffDate = subDays(new Date(), days);
    return data
      .filter(d => new Date(d.activity_date) >= cutoffDate)
      .map(d => ({
        ...d,
        date: format(parseISO(d.activity_date), 'MMM dd'),
        fullDate: d.activity_date,
      }));
  };

  const getWeeklyData = () => {
    const filtered = getFilteredData();
    const weeks = new Map<string, any>();

    filtered.forEach(d => {
      const date = parseISO(d.activity_date);
      const weekStart = startOfWeek(date);
      const weekKey = format(weekStart, 'MMM dd');

      if (!weeks.has(weekKey)) {
        weeks.set(weekKey, {
          week: weekKey,
          sessions_created: 0,
          messages_sent: 0,
          memories_created: 0,
          projects_created: 0,
          boards_created: 0,
          total_activity: 0,
        });
      }

      const weekData = weeks.get(weekKey)!;
      weekData.sessions_created += Number(d.sessions_created);
      weekData.messages_sent += Number(d.messages_sent);
      weekData.memories_created += Number(d.memories_created);
      weekData.projects_created += Number(d.projects_created || 0);
      weekData.boards_created += Number(d.boards_created || 0);
      weekData.total_activity += Number(d.total_activity);
    });

    return Array.from(weeks.values());
  };

  const dailyData = getFilteredData();
  const weeklyData = getWeeklyData();

  const getTotalStats = () => {
    const filtered = getFilteredData();
    return {
      totalSessions: filtered.reduce((sum, d) => sum + Number(d.sessions_created), 0),
      totalMessages: filtered.reduce((sum, d) => sum + Number(d.messages_sent), 0),
      totalMemories: filtered.reduce((sum, d) => sum + Number(d.memories_created), 0),
      totalProjects: filtered.reduce((sum, d) => sum + Number(d.projects_created || 0), 0),
      totalBoards: filtered.reduce((sum, d) => sum + Number(d.boards_created || 0), 0),
      totalActivity: filtered.reduce((sum, d) => sum + Number(d.total_activity), 0),
      avgDaily: filtered.length > 0 ? Math.round(filtered.reduce((sum, d) => sum + Number(d.total_activity), 0) / filtered.length) : 0,
    };
  };

  const stats = getTotalStats();

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{title}</CardTitle>
            {description && <CardDescription>{description}</CardDescription>}
          </div>
          <Tabs value={timeRange} onValueChange={(v) => setTimeRange(v as any)} className="w-auto">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="7d">7D</TabsTrigger>
              <TabsTrigger value="30d">30D</TabsTrigger>
              <TabsTrigger value="90d">90D</TabsTrigger>
              <TabsTrigger value="365d">1Y</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Summary */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Total Activity</p>
            <p className="text-2xl font-bold">{stats.totalActivity}</p>
            <p className="text-xs text-muted-foreground">{stats.avgDaily} avg/day</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Sessions</p>
            <p className="text-2xl font-bold">{stats.totalSessions}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Messages</p>
            <p className="text-2xl font-bold">{stats.totalMessages}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Memories</p>
            <p className="text-2xl font-bold">{stats.totalMemories}</p>
          </div>
        </div>

        {/* Charts */}
        <Tabs defaultValue="area" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="area">Area</TabsTrigger>
            <TabsTrigger value="bar">Bar</TabsTrigger>
            <TabsTrigger value="line">Line</TabsTrigger>
          </TabsList>

          <TabsContent value="area" className="space-y-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={timeRange === "365d" ? weeklyData : dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey={timeRange === "365d" ? "week" : "date"} 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="sessions_created" 
                    stackId="1"
                    stroke="hsl(var(--primary))" 
                    fill="hsl(var(--primary))" 
                    name="Sessions"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="messages_sent" 
                    stackId="1"
                    stroke="hsl(var(--chart-2))" 
                    fill="hsl(var(--chart-2))" 
                    name="Messages"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="memories_created" 
                    stackId="1"
                    stroke="hsl(var(--chart-3))" 
                    fill="hsl(var(--chart-3))" 
                    name="Memories"
                  />
                  {showProjects && (
                    <Area 
                      type="monotone" 
                      dataKey="projects_created" 
                      stackId="1"
                      stroke="hsl(var(--chart-4))" 
                      fill="hsl(var(--chart-4))" 
                      name="Projects"
                    />
                  )}
                  {showBoards && (
                    <Area 
                      type="monotone" 
                      dataKey="boards_created" 
                      stackId="1"
                      stroke="hsl(var(--chart-5))" 
                      fill="hsl(var(--chart-5))" 
                      name="Boards"
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="bar" className="space-y-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={timeRange === "365d" ? weeklyData : dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey={timeRange === "365d" ? "week" : "date"} 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Bar dataKey="sessions_created" fill="hsl(var(--primary))" name="Sessions" />
                  <Bar dataKey="messages_sent" fill="hsl(var(--chart-2))" name="Messages" />
                  <Bar dataKey="memories_created" fill="hsl(var(--chart-3))" name="Memories" />
                  {showProjects && <Bar dataKey="projects_created" fill="hsl(var(--chart-4))" name="Projects" />}
                  {showBoards && <Bar dataKey="boards_created" fill="hsl(var(--chart-5))" name="Boards" />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>

          <TabsContent value="line" className="space-y-4">
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={timeRange === "365d" ? weeklyData : dailyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey={timeRange === "365d" ? "week" : "date"} 
                    className="text-xs"
                    tick={{ fill: 'hsl(var(--muted-foreground))' }}
                  />
                  <YAxis className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--popover))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px',
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="sessions_created" stroke="hsl(var(--primary))" name="Sessions" strokeWidth={2} />
                  <Line type="monotone" dataKey="messages_sent" stroke="hsl(var(--chart-2))" name="Messages" strokeWidth={2} />
                  <Line type="monotone" dataKey="memories_created" stroke="hsl(var(--chart-3))" name="Memories" strokeWidth={2} />
                  {showProjects && <Line type="monotone" dataKey="projects_created" stroke="hsl(var(--chart-4))" name="Projects" strokeWidth={2} />}
                  {showBoards && <Line type="monotone" dataKey="boards_created" stroke="hsl(var(--chart-5))" name="Boards" strokeWidth={2} />}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
